import { useState, useRef, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { X, Copy, CheckCircle2, ArrowUpCircle, DollarSign, Zap, Loader2, Upload, ExternalLink, Sparkles } from 'lucide-react';
import { Input } from '../../components/ui/input';
import PhoneVerificationGate from './PhoneVerificationGate';
import { resolvePaymentBrandLogo, resolvePaymentLogoShape } from '../../lib/paymentBrands';
import { resolveAssetUrl } from '../../lib/utils';
import { trackEvent } from '../../lib/analytics';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const MIN_WITHDRAW_AMOUNT = 30;

// Brand-accurate SVG icons (Simple Icons CC0-licensed paths, inlined as data URIs).
// These are the real wordmarks used by each brand — no external CDN, crisp at any DPI.
const BRAND_ICONS = {
  cashapp: "data:image/svg+xml;utf8," + encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><rect width='24' height='24' rx='5' fill='#00D632'/><path d='M17.82 8.04c-.25.25-.65.26-.9.01-.65-.65-1.5-1.06-2.42-1.06-.72 0-1.28.28-1.28.8 0 .48.42.68 1.5 1.04l.76.26c2.14.72 3.44 1.76 3.44 3.6 0 1.88-1.46 3.22-3.84 3.36l-.22 1.52c-.08.56-.56.96-1.12.92l-.02-.004c-.52-.08-.88-.56-.82-1.08l.22-1.52c-1.34-.26-2.44-.88-3.2-1.84-.24-.3-.22-.74.08-.98l.1-.08c.28-.2.66-.16.88.12.76.96 1.72 1.4 2.74 1.4.86 0 1.46-.34 1.46-.86 0-.52-.42-.74-1.68-1.16l-.66-.22c-2.04-.68-3.28-1.76-3.28-3.48 0-1.84 1.42-3.12 3.7-3.28l.22-1.52c.08-.52.56-.88 1.08-.82.52.08.88.56.82 1.08l-.22 1.52c1 .22 1.82.64 2.46 1.2.3.26.32.72.06.98z' fill='#fff'/></svg>`
  ),
  applepay: "data:image/svg+xml;utf8," + encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><rect width='24' height='24' rx='5' fill='#000'/><path d='M6.17 8.99c.34.43.87.76 1.4.76.06 0 .12 0 .18-.01.09-.58-.18-1.17-.52-1.54-.33-.36-.88-.63-1.33-.65-.07.57.21 1.14.27 1.44zm1.57.97c-.73 0-1.35.41-1.7.41-.37 0-.9-.39-1.48-.38-.76.01-1.46.44-1.85 1.13-.79 1.37-.2 3.4.57 4.51.37.55.82 1.15 1.4 1.13.56-.02.77-.36 1.45-.36.67 0 .87.36 1.46.35.61-.01 1-.55 1.37-1.1.43-.63.6-1.24.61-1.27-.01-.01-1.18-.46-1.19-1.8-.01-1.13.92-1.67.96-1.69-.53-.77-1.34-.86-1.62-.88-.74-.06-1.36.41-1.7.41-.01 0-.01 0 0 0v.14zm5.78-1.7h-1.23v6.88h.86v-2.35h1.19c1.09 0 1.85-.75 1.85-1.83-.01-1.03-.76-1.7-1.64-1.7h-1.03zm.63 2.96h-1v-2.17h1c.62 0 1 .34 1 1.08s-.37 1.09-1 1.09zm3.83-.65c-.82.05-1.48.53-1.48 1.25 0 .73.54 1.21 1.3 1.21.5 0 .97-.27 1.18-.69h.02v.64h.82V10.9c0-.82-.65-1.35-1.65-1.35-.93 0-1.62.53-1.65 1.26h.79c.07-.35.39-.57.83-.57.54 0 .84.25.84.72v.31l-1 .06zm1 .65c0 .53-.46.91-1.04.91-.46 0-.74-.22-.74-.56 0-.35.28-.55.82-.59l.96-.05v.29zm3.39 2.66h.88l1.96-5.47h-.89l-1.28 4.03h-.02l-1.28-4.03h-.91l1.84 5.04-.1.31c-.17.52-.43.72-.89.72-.08 0-.24-.01-.3-.02v.67c.06.02.29.02.37.02.83 0 1.22-.32 1.56-1.27z' fill='#fff'/></svg>`
  ),
  googlepay: "data:image/svg+xml;utf8," + encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><rect width='24' height='24' rx='5' fill='#fff'/><path d='M11.5 11.4v1.95h2.7c-.11.64-.44 1.19-.94 1.55v1.29h1.52c.89-.82 1.4-2.03 1.4-3.47 0-.34-.03-.66-.08-.98h-4.6z' fill='#4285F4'/><path d='M11.5 16.5c1.27 0 2.33-.42 3.1-1.14l-1.51-1.17c-.42.28-.96.45-1.59.45-1.22 0-2.25-.82-2.62-1.93H7.32v1.21c.77 1.52 2.34 2.58 4.18 2.58z' fill='#34A853'/><path d='M8.88 12.71c-.09-.28-.15-.58-.15-.88s.05-.6.15-.88v-1.21H7.32c-.3.6-.48 1.28-.48 2.09s.17 1.49.48 2.09l1.56-1.21z' fill='#FBBC05'/><path d='M11.5 9.03c.69 0 1.3.24 1.79.7l1.34-1.34c-.81-.75-1.87-1.21-3.13-1.21-1.84 0-3.41 1.06-4.18 2.58l1.56 1.21c.37-1.11 1.4-1.94 2.62-1.94z' fill='#EA4335'/><text x='17' y='15' font-family='-apple-system,Segoe UI,sans-serif' font-size='3' font-weight='600' fill='#5F6368'>Pay</text></svg>`
  ),
  apple_pay: null,
  google_pay: null,
  btc: "data:image/svg+xml;utf8," + encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><rect width='24' height='24' rx='5' fill='#F7931A'/><path d='M16.4 10.76c.22-1.45-.88-2.23-2.37-2.75l.48-1.94-1.18-.29-.47 1.88c-.31-.08-.63-.15-.95-.22l.47-1.89-1.18-.29-.48 1.94c-.25-.06-.5-.11-.75-.17l-1.63-.4-.31 1.26s.87.2.85.21c.48.12.56.44.55.69l-.56 2.22c.03.01.08.02.13.04l-.13-.03-.78 3.12c-.06.15-.21.37-.56.28 0 .02-.85-.21-.85-.21l-.59 1.36 1.54.38c.29.07.57.14.85.21l-.49 1.97 1.18.29.48-1.94c.32.09.64.17.95.25l-.48 1.93 1.18.29.48-1.96c2.02.38 3.55.23 4.19-1.6.52-1.48-.03-2.33-1.08-2.89.77-.18 1.34-.68 1.5-1.71zm-2.68 3.82c-.37 1.48-2.89.68-3.71.48l.64-2.57c.81.2 3.46.59 3.07 2.09zm.37-3.88c-.34 1.35-2.44.66-3.12.49l.58-2.32c.68.17 2.89.48 2.54 1.83z' fill='#fff'/></svg>`
  ),
  bitcoin: null,
  paypal: "data:image/svg+xml;utf8," + encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><rect width='24' height='24' rx='5' fill='#003087'/><path d='M16.4 8.1c.2 1.32-.68 2.89-2.66 2.89h-1.1l-.36 2.22h-1.04l-.07.5h2l.18-1.24c.07-.52.52-.88 1.04-.88h.58c2.08 0 2.96-1.6 2.62-2.99-.22-.94-1.1-1.56-2.34-1.56H12.2c-.3 0-.52.18-.56.48l-1.16 7h1.72l.4-2.56h1.46c1.56 0 2.6-.98 2.82-2.7.05-.18.05-.36 0-.48.16.08.52.18.52-.3zM7.6 6.1c-.3 0-.52.18-.56.48l-1.1 6.54h1.72l.4-2.44h1.1c2.08 0 2.96-1.6 2.62-2.99-.22-.94-1.1-1.56-2.34-1.56h-1.84z' fill='#fff'/><path d='M10.52 8.3c-.18.98-.92 1.46-1.86 1.46H8.04l.4-2.42h.62c.88 0 1.64.3 1.46.96z' fill='#009CDE'/></svg>`
  ),
  // Multi-coin "Crypto" badge for NOWPayments — three overlapping coins
  // (BTC orange, ETH violet, USDT green) on a deep navy plate. Instantly
  // signals "multiple coins supported" without locking the brand to BTC.
  crypto: "data:image/svg+xml;utf8," + encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>` +
    `<defs><linearGradient id='cg' x1='0' y1='0' x2='1' y2='1'>` +
    `<stop offset='0' stop-color='#1e293b'/><stop offset='1' stop-color='#0f172a'/></linearGradient></defs>` +
    `<rect width='24' height='24' rx='5' fill='url(#cg)'/>` +
    // USDT (back, green)
    `<circle cx='15.4' cy='12' r='4.4' fill='#26A17B'/>` +
    `<text x='15.4' y='13.6' font-family='Arial,sans-serif' font-size='4.6' font-weight='700' fill='#fff' text-anchor='middle'>₮</text>` +
    // ETH (middle, violet)
    `<circle cx='12' cy='12' r='4.4' fill='#627EEA'/>` +
    `<path d='M12 8.4 L9.4 12.2 L12 13.7 L14.6 12.2 Z' fill='#fff' opacity='0.95'/>` +
    `<path d='M12 14.4 L9.4 12.8 L12 16.4 L14.6 12.8 Z' fill='#fff' opacity='0.7'/>` +
    // BTC (front, orange)
    `<circle cx='8.6' cy='12' r='4.4' fill='#F7931A'/>` +
    `<text x='8.6' y='13.6' font-family='Arial,sans-serif' font-size='4.8' font-weight='700' fill='#fff' text-anchor='middle'>₿</text>` +
    `</svg>`
  ),
};
// Alias resolutions
BRAND_ICONS.apple_pay = BRAND_ICONS.applepay;
BRAND_ICONS.google_pay = BRAND_ICONS.googlepay;
BRAND_ICONS.bitcoin = BRAND_ICONS.btc;
BRAND_ICONS.nowpayments = BRAND_ICONS.crypto;

const resolveLogo = (m) => {
  const uploaded = m.logo_url || m.qr_code_url || m.qr_image_url;
  if (uploaded) return uploaded.startsWith('http') ? uploaded : `${API_BASE}${uploaded}`;
  return resolvePaymentBrandLogo(m);
};

const PaymentMethodLogo = ({ method }) => {
  const src = resolveLogo(method);
  const shape = resolvePaymentLogoShape(src, method);
  if (!src) {
    return (
      <div className="flex h-12 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-[#D4AF37]/15">
        <DollarSign className="h-6 w-6 text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className={`${shape === 'wordmark' ? 'w-[74px]' : 'w-14'} flex h-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white px-2.5 shadow-[0_10px_24px_rgba(0,0,0,0.22)]`}>
      <img
        src={src}
        alt={method.title || method.name || 'Payment method'}
        className={`${shape === 'wordmark' ? 'max-h-8 max-w-[58px]' : 'max-h-9 max-w-9'} h-auto w-auto object-contain`}
      />
    </div>
  );
};

// Default provider-approved deposit amount presets, used when the admin
// has not configured per-method `allowed_amounts` on the payment_method doc.
// Amounts are in USD and map to the upstream provider (Linkgen) route map.
// Linkgen handles Cash App, Apple Pay, Google Pay, and Chime.
// Crypto deposits are handled by VegasLobby through NOWPayments, not Linkgen.
// Cash App / Apple Pay can use the Gears-style ladder; Google Pay uses the
// standard wallet ladder because Gears Pay Google Pay is not always configured.
const AMOUNTS_APPLE_GOOGLE_BTC_XXPAY = [
  9.99, 14.99, 17.99, 19.99, 24.99, 29.99, 30.99, 39.99, 49.99, 59.99,
  99.99, 124.99, 129.99, 149.99, 199.99, 300, 400, 500,
];
const AMOUNTS_GEARSPAY_CASHAPP = [
  4.99, 5.99, 6.99, 7.99, 8.99, 9.99, 10.99, 11.99, 12.99, 13.99, 14.99,
  17.99, 19.99, 24.99, 29.99, 30.99, 39.99, 49.99, 59.99, 99.99,
  124.99, 129.99, 149.99, 199.99, 249.99, 299.99, 399.99, 499.99,
];
const AMOUNTS_LINKGEN_CHIME = [
  20, 25, 30, 31, 40, 50, 60, 100, 125, 130, 150, 200, 300, 400, 500,
];

const normalizeAmountList = (amounts = []) => {
  const seen = new Set();
  return amounts
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0)
    .map((v) => Number(v.toFixed(2)))
    .filter((v) => {
      const key = v.toFixed(2);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a - b);
};

const resolveDefaultAmounts = (m) => {
  if (!m) return null;
  const code = (m.gateway_method_code || m.method || m.code || '').toLowerCase().replace(/[_\s-]/g, '');
  const hint = [m.name, m.title, m.gateway_provider, m.provider_hint]
    .filter(Boolean).join(' ').toLowerCase();
  const isLinkgen = hint.includes('linkgen') || (m.gateway_provider || '').toLowerCase() === 'linkgen';
  if (code === 'cashapp' || code === 'cash_app') {
    if (hint.includes('gears')) return AMOUNTS_GEARSPAY_CASHAPP;
    if (isLinkgen) return AMOUNTS_APPLE_GOOGLE_BTC_XXPAY;
    return AMOUNTS_APPLE_GOOGLE_BTC_XXPAY;
  }
  if (code === 'applepay' || code === 'apple_pay') {
    if (hint.includes('gears')) return AMOUNTS_GEARSPAY_CASHAPP;
    return AMOUNTS_APPLE_GOOGLE_BTC_XXPAY;
  }
  if (code === 'googlepay' || code === 'google_pay') {
    return AMOUNTS_APPLE_GOOGLE_BTC_XXPAY;
  }
  if (code === 'chime') {
    return AMOUNTS_LINKGEN_CHIME;
  }
  if (code === 'crypto' || code === 'nowpayments') {
    return AMOUNTS_APPLE_GOOGLE_BTC_XXPAY;
  }
  return null;
};

const resolveAllowedAmounts = (m, isInstant = true) => {
  if (!m || !isInstant) return null;
  const adminAmounts = Array.isArray(m?.allowed_amounts) && m.allowed_amounts.length > 0
    ? m.allowed_amounts
    : null;
  const providerDefaultAmounts = resolveDefaultAmounts(m);
  return (adminAmounts || providerDefaultAmounts)
    ? normalizeAmountList(adminAmounts || providerDefaultAmounts || [])
    : null;
};

const amountAllowedForMethod = (m, amountValue) => {
  const list = resolveAllowedAmounts(m, m?.payment_mode === 'GATEWAY');
  if (!list) return true;
  const requested = Number(amountValue);
  return Number.isFinite(requested) && list.some((v) => Math.abs(v - requested) < 0.005);
};

const flowForMode = (mode) => (mode === 'withdrawal' ? 'withdraw' : 'deposit');

const payoutCopyForMethod = (method = {}) => {
  const name = method.title || method.name || 'payment method';
  const key = `${name} ${method.method || ''} ${method.gateway_method_code || ''}`.toLowerCase();

  if (key.includes('chime')) {
    return {
      title: 'Where should we send your Chime withdrawal?',
      primaryLabel: 'Chime tag / username',
      primaryPlaceholder: '$YourChimeTag or Chime username',
      secondaryLabel: 'Phone or email linked to Chime',
      secondaryPlaceholder: 'Optional',
      qrHint: 'Upload your Chime QR if you want admin to scan instead of typing.',
    };
  }
  if (key.includes('cash')) {
    return {
      title: 'Where should we send your Cash App withdrawal?',
      primaryLabel: 'Cash App cashtag',
      primaryPlaceholder: '$YourCashtag',
      secondaryLabel: 'Cash App phone or email',
      secondaryPlaceholder: 'Optional',
      qrHint: 'Upload your Cash App QR to avoid payout typing mistakes.',
    };
  }
  if (key.includes('apple')) {
    return {
      title: 'Where should we send your Apple Pay withdrawal?',
      primaryLabel: 'Apple Pay phone or email',
      primaryPlaceholder: 'Phone number or email linked to Apple Pay',
      secondaryLabel: 'Recipient name',
      secondaryPlaceholder: 'Optional',
      qrHint: 'Upload your Apple Pay QR if you prefer QR payout.',
    };
  }
  if (key.includes('google')) {
    return {
      title: 'Where should we send your Google Pay withdrawal?',
      primaryLabel: 'Google Pay phone or email',
      primaryPlaceholder: 'Phone number or email linked to Google Pay',
      secondaryLabel: 'Recipient name',
      secondaryPlaceholder: 'Optional',
      qrHint: 'Upload your Google Pay QR if you prefer QR payout.',
    };
  }
  if (key.includes('btc') || key.includes('bitcoin') || key.includes('crypto') || key.includes('usdt')) {
    return {
      title: `Where should we send your ${name} withdrawal?`,
      primaryLabel: 'Wallet address',
      primaryPlaceholder: 'Paste your wallet address',
      secondaryLabel: 'Network / memo / tag',
      secondaryPlaceholder: 'Example: TRC20, ERC20, memo, tag',
      qrHint: 'Upload your wallet QR so admin can scan the destination.',
    };
  }
  return {
    title: `Where should we send your ${name} withdrawal?`,
    primaryLabel: 'Payout address / username',
    primaryPlaceholder: `Your ${name} address, tag, phone, or username`,
    secondaryLabel: 'Memo / tag',
    secondaryPlaceholder: 'Optional',
    qrHint: 'Upload a payout QR if this method supports QR payments.',
  };
};

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0; const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Deposit / Withdrawal popup. Lives INSIDE the player-app main area via
 * `absolute inset-0` so it auto-fits between top bar and bottom nav.
 *
 * Props: mode = 'deposit' | 'withdrawal' | null
 */
export default function DepositWithdrawPopup({ mode, onClose, user, wallet, methods = [], refresh, checkAuth, onAfterSubmit }) {
  const [activeMode, setActiveMode] = useState(mode || 'deposit');
  const [popupStep, setPopupStep] = useState('method');
  const [amount, setAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [processing, setProcessing] = useState(false);
  // Phone-verification gate — mounted inline so it overlays the withdraw popup.
  const [showPhoneGate, setShowPhoneGate] = useState(false);
  const [payoutDetails, setPayoutDetails] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [payoutTag, setPayoutTag] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [qrFile, setQrFile] = useState(null);  // iter 128: player's payment QR for withdrawals
  const proofInputRef = useRef(null);
  const qrInputRef = useRef(null);

  // Gateway-flow state
  const [gatewayState, setGatewayState] = useState(null);
  const [checkoutFrameLoading, setCheckoutFrameLoading] = useState(false);
  // gatewayState = { depositId, payUrl, status, usd_amount, methodTitle } | null
  const pollRef = useRef(null);

  useEffect(() => {
    if (!mode) return;
    trackEvent('wallet_popup_opened', { mode });
    setActiveMode(mode);
    setPopupStep('method');
    setAmount('');
    setSelectedMethod(null);
    setPayoutDetails('');
    setWalletAddress('');
    setPayoutTag('');
    setProofFile(null);
    setQrFile(null);
    setGatewayState(null);
    setCheckoutFrameLoading(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, [mode]);

  // Stop polling when popup closes/unmounts
  useEffect(() => {
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, []);

  if (!mode) return null;

  const reset = () => {
    setPopupStep('method');
    setAmount(''); setSelectedMethod(null); setPayoutDetails('');
    setWalletAddress(''); setPayoutTag(''); setProofFile(null); setQrFile(null);
    setGatewayState(null);
    setCheckoutFrameLoading(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  const close = () => { reset(); onClose?.(); };
  const currentMode = activeMode || mode || 'deposit';
  const switchFlow = (nextMode) => {
    if (nextMode === currentMode || processing || gatewayState) return;
    trackEvent('wallet_popup_mode_changed', { from: currentMode, to: nextMode });
    setActiveMode(nextMode);
    setPopupStep('method');
    setAmount('');
    setSelectedMethod(null);
    setPayoutDetails('');
    setWalletAddress('');
    setPayoutTag('');
    setProofFile(null);
    setQrFile(null);
  };

  const isDeposit = currentMode === 'deposit';
  const flowKey = flowForMode(currentMode);
  const filteredMethods = methods.filter((m) => {
    const methodFlow = (m.flow || 'both').toLowerCase();
    return methodFlow === 'both' || methodFlow === flowKey || (flowKey === 'withdraw' && methodFlow === 'withdrawal');
  });
  const visibleMethods = filteredMethods.filter(m => m.upstream_status !== 'broken');
  const payoutCopy = !isDeposit && selectedMethod ? payoutCopyForMethod(selectedMethod) : null;
  const accent = isDeposit ? '#D9A62E' : '#22c55e';
  const accentGradient = isDeposit
    ? 'linear-gradient(135deg, #F5D76E, #C58A18)'
    : 'linear-gradient(135deg, #22c55e, #16a34a)';
  const walletBalance = Number(wallet?.balance ?? wallet?.cash_balance ?? user?.wallet_balance ?? user?.balance ?? 0);
  const parsedAmount = parseFloat(amount);
  const hasValidAmount = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const withdrawBelowMinimum = !isDeposit && hasValidAmount && parsedAmount < MIN_WITHDRAW_AMOUNT;
  const showMethodStep = popupStep === 'method';
  const showAmountStep = popupStep === 'amount';
  const showPayoutStep = popupStep === 'payout';
  const submitReady = isDeposit
    ? selectedMethod && hasValidAmount
    : selectedMethod && hasValidAmount && (walletAddress.trim() || payoutTag.trim() || qrFile);
  const totalSteps = isDeposit ? 2 : 3;
  const stepLabel = showMethodStep ? `1 of ${totalSteps}` : showAmountStep ? `2 of ${totalSteps}` : `3 of ${totalSteps}`;
  const gatewayStatus = String(gatewayState?.status || '').toUpperCase();
  const checkoutApproved = ['APPROVED', 'SUCCESS', 'PAID', 'COMPLETED', 'SETTLED'].includes(gatewayStatus);
  const checkoutFailed = ['FAILED', 'EXPIRED', 'CANCELLED', 'CANCELED'].includes(gatewayStatus);
  const copyGatewayLink = async () => {
    if (!gatewayState?.payUrl) return;
    try {
      await navigator.clipboard?.writeText(gatewayState.payUrl);
      toast.success('Checkout link copied');
    } catch {
      toast.error('Could not copy checkout link');
    }
  };
  const openGatewayLink = () => {
    if (!gatewayState?.payUrl) return;
    window.open(gatewayState.payUrl, '_blank', 'noopener,noreferrer');
  };

  // ─────────── Poll the gateway status every 1s for instant wallet credit ───────────
  // 1 s cadence means the moment the provider confirms the payment on their
  // side we see it here and auto-load the player's wallet. We also stop
  // polling automatically on APPROVED / FAILED / EXPIRED so we don't bang
  // against the backend forever.
  const startPolling = (depositId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      // Safety cap — 20 minutes is plenty even for slow bank rails.
      if (attempts > 1200) { clearInterval(pollRef.current); pollRef.current = null; return; }
      try {
        const res = await api.post(`/api/deposits/${depositId}/poll-status`);
        const r = res.data;
        const nextStatus = String(r.status || r.gateway_status || '').toUpperCase();
        setGatewayState((prev) => prev ? { ...prev, status: nextStatus || prev.status } : prev);
        if (['APPROVED', 'SUCCESS', 'PAID', 'COMPLETED', 'SETTLED'].includes(nextStatus)) {
          clearInterval(pollRef.current); pollRef.current = null;
          // Iter 137: trigger the celebratory SuccessBurst overlay — coin
          // shower + tier-colored ring around a checkmark, then the popup
          // closes itself (auto-handled in PlayerApp).
          if (typeof window !== 'undefined' && window.__vlTriggerBurst) {
            window.__vlTriggerBurst({
              title: 'Deposit confirmed!',
              subtitle: `$${parseFloat(amount || 0).toFixed(2)} added to your wallet`,
              color: '#22c55e',
              duration: 2200,
            });
          } else {
            toast.success('Deposit confirmed! Wallet loaded.');
          }
          refresh?.(); await checkAuth?.();
        } else if (['FAILED', 'EXPIRED', 'CANCELLED', 'CANCELED'].includes(nextStatus)) {
          clearInterval(pollRef.current); pollRef.current = null;
          toast.error(nextStatus === 'EXPIRED' ? 'Payment link expired' : 'Payment failed');
        }
      } catch {
        // keep polling — transient network errors are fine
      }
    }, 1000);
  };

  // No pre-opened tab/redirect needed — the provider checkout is embedded
  // inside this popup so the player stays in the VegasLobby app shell.

  const handleGatewayDeposit = async (methodOverride = null) => {
    const usdAmount = parseFloat(amount);
    if (!amount || usdAmount <= 0) { toast.error('Enter valid amount'); return; }
    const methodToUse = methodOverride || selectedMethod;
    if (!methodToUse) { toast.error('Select payment method'); return; }
    if (!amountAllowedForMethod(methodToUse, usdAmount)) {
      const allowed = resolveAllowedAmounts(methodToUse, true) || [];
      const min = allowed[0];
      const max = allowed[allowed.length - 1];
      const methodName = methodToUse.title || methodToUse.name || 'This method';
      toast.error(
        `${methodName} does not support $${usdAmount.toFixed(2)}. Pick an approved amount${min ? ` from $${min.toFixed(2)} to $${max.toFixed(2)}` : ''}.`,
        { duration: 9000 }
      );
      return;
    }

    // Keep the player inside the deposit popup while we fetch the checkout URL.
    // `processing` drives a full-screen spinner overlay ("Generating checkout
    // URL…") so there is no blank about:blank tab confusion.
    setProcessing(true);
    trackEvent('deposit_gateway_link_requested', {
      amount: usdAmount,
      payment_method: methodToUse?.method || methodToUse?.code || methodToUse?.gateway_method_code,
    });
    try {
      const methodCode = methodToUse.gateway_method_code
        || methodToUse.gateway_method
        || methodToUse.method
        || methodToUse.code;
      const res = await api.post(
        '/api/gateway/deposit',
        { amount: usdAmount, method: methodCode, currency: 'USD' },
        { headers: { 'X-Idempotency-Key': `gw-dep-${uuid()}` } }
      );
      const d = res.data;
      if (!d.pay_url) throw new Error('Checkout link was not returned');
      trackEvent('deposit_gateway_link_created', {
        amount: usdAmount,
        payment_method: methodCode,
        fallback_used: d.fallback_used,
      });

      // If the backend silently fell back to another method because the
      // chosen one's upstream was broken, let the player know so they see
      // a different branded checkout page instead of assuming something
      // went wrong.
      if (d.fallback_used && d.fallback_from && d.fallback_used !== d.fallback_from) {
        const prettyFrom = String(d.fallback_from).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const prettyTo = String(d.fallback_used).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        toast.success(`${prettyFrom} unavailable — loaded via ${prettyTo} instead`);
      }

      setGatewayState({
        depositId: d.order_id,
        payUrl: d.pay_url,
        status: d.status || 'PENDING',
        usd_amount: d.charged_amount || usdAmount,
        methodTitle: methodToUse.title || methodToUse.name || 'Secure checkout',
      });
      setCheckoutFrameLoading(true);
      setProcessing(false);
      startPolling(d.order_id);
      toast.success('Secure checkout opened inside Vegas Lobby');
    } catch (err) {
      setProcessing(false);
      trackEvent('deposit_gateway_link_failed', {
        amount: usdAmount,
        payment_method: methodToUse?.method || methodToUse?.code || methodToUse?.gateway_method_code,
      });
      // Friendly failure message — no raw upstream errors to the player.
      // Extract the alternate-method suggestion the backend appends after a
      // " | Try instead: X, Y" marker when Linkgen's routing falls through.
      let rawMsg = err.response?.data?.detail
        || err.response?.data?.message
        || err.response?.data?.error
        || err.message
        || '';
      console.warn('Gateway deposit failed:', rawMsg);
      const suggestMatch = typeof rawMsg === 'string'
        ? rawMsg.match(/\|\s*Try instead:\s*(.+?)$/i)
        : null;
      const alternates = suggestMatch ? suggestMatch[1].trim() : '';
      const methodDisplay = methodToUse?.title || methodToUse?.name || 'This method';

      if (/payment method .+ not found|not enabled/i.test(rawMsg)) {
        toast.error(`${methodDisplay} isn't available right now — please pick another payment method.`);
      } else if (alternates) {
        const firstAlt = alternates.split(',')[0].trim();
        const altMethodObj = methods.find((mth) => {
          const t = (mth.title || mth.name || '').toLowerCase();
          const c = (mth.code || mth.method || '').toLowerCase();
          const target = firstAlt.toLowerCase();
          return t === target || c === target.replace(/\s+/g, '_') || c === target.replace(/\s+/g, '');
        });
        toast.error(
          `${methodDisplay} couldn't generate a link for this amount. Try ${alternates} instead.`,
          {
            duration: 12000,
            action: altMethodObj ? {
              label: `Use ${firstAlt}`,
              onClick: () => {
                setSelectedMethod(altMethodObj);
                if (!amountAllowedForMethod(altMethodObj, amount)) {
                  setAmount('');
                  toast.message(`Pick an approved ${firstAlt} amount before generating the link.`);
                }
              },
            } : undefined,
          }
        );
      } else if (/timeout|cooldown|automatic providers/i.test(rawMsg)) {
        toast.error(
          `Our payment provider is briefly unreachable. Please try again in a few seconds, or pick another method.`,
          { duration: 8000 }
        );
      } else {
        toast.error(
          `Sorry, checkout link generation failed. Please proceed with manual load — our support is live 24/7.`,
          { duration: 7000 }
        );
      }
    }
  };

  const handleManualDeposit = async () => {
    const usdAmount = parseFloat(amount);
    if (!amount || usdAmount <= 0) { toast.error('Enter valid amount'); return; }
    if (!selectedMethod) { toast.error('Select payment method'); return; }
    if (!proofFile) {
      toast.error('Upload your payment screenshot before submitting a manual deposit');
      return;
    }
    setProcessing(true);
    try {
      const res = await api.post('/api/requests/deposit', { amount: usdAmount, payment_method_id: selectedMethod.id });
      const txnId = res.data.id;
      if (proofFile && txnId) {
        // Send the real file as multipart/form-data so the backend can
        // compress + persist it AND re-dispatch the Telegram bot message
        // with the actual image attached.
        try {
          const fd = new FormData();
          fd.append('file', proofFile);
          await api.post(`/api/requests/deposit/${txnId}/upload-proof`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (uploadErr) {
          // Non-fatal: deposit was already created. Let the player know
          // they can retry from the Transactions list.
          toast.error('Deposit created, but the screenshot upload failed. Please resend the proof to support.');
        }
      }
      // Signal the parent (PlayerApp) to show the friendly "please wait, your
      // wallet is being loaded" modal. PlayerApp will watch the wallet
      // balance and auto-dismiss with the "loaded!" celebration.
      toast.success(`Deposit request submitted for $${usdAmount.toFixed(2)} — awaiting approval`);
      trackEvent('deposit_manual_submitted', {
        amount: usdAmount,
        payment_method: selectedMethod?.method || selectedMethod?.code,
        proof_attached: Boolean(proofFile),
      });
      onAfterSubmit?.('deposit', { usdAmount, txnId });
      close(); refresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Deposit failed');
    } finally { setProcessing(false); }
  };

  const handleDeposit = async () => {
    if (selectedMethod?.payment_mode === 'GATEWAY') return handleGatewayDeposit();
    return handleManualDeposit();
  };

  const handleWithdraw = async () => {
    const usdAmount = parseFloat(amount);
    if (!amount || usdAmount <= 0) { toast.error('Enter valid amount'); return; }
    if (usdAmount < MIN_WITHDRAW_AMOUNT) { toast.error(`Minimum withdrawal is $${MIN_WITHDRAW_AMOUNT}`); return; }
    if (!selectedMethod) { toast.error('Select payment method'); return; }
    if (!walletAddress.trim() && !payoutTag.trim() && !qrFile) {
      toast.error('Enter payout details or upload your payment QR');
      return;
    }
    setProcessing(true);
    try {
      const res = await api.post('/api/requests/withdraw', {
        amount: usdAmount,
        payment_method_id: selectedMethod.id,
        payout_details: {
          method: selectedMethod.method,
          method_title: selectedMethod.title || selectedMethod.name,
          address: walletAddress.trim(),
          tag: payoutTag.trim(),
          notes: payoutDetails.trim(),
          qr_attached: !!qrFile,
        }
      });
      const txnId = res.data?.id;
      // iter 128: if the player attached a QR image, ship it up right away
      // so admins see it in their queue without waiting for support emails.
      if (qrFile && txnId) {
        try {
          const fd = new FormData();
          fd.append('file', qrFile);
          await api.post(`/api/requests/withdraw/${txnId}/upload-qr`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (uploadErr) {
          // Non-fatal — request already submitted.
          toast.error('Withdrawal submitted, but QR image upload failed. Reach out to support with your QR.');
        }
      }
      toast.success(`Withdrawal request submitted for $${usdAmount.toFixed(2)} — awaiting approval`);
      trackEvent('withdrawal_submitted', {
        amount: usdAmount,
        payment_method: selectedMethod?.method || selectedMethod?.code,
        qr_attached: Boolean(qrFile),
      });
      close(); refresh?.(); await checkAuth?.(); onAfterSubmit?.('withdrawal');
    } catch (err) {
      // Phone verification gate — surface the dedicated dialog instead of
      // a generic toast so the player can see the WhatsApp CTA.
      const errData = err?.response?.data || {};
      const detail = errData.detail && typeof errData.detail === 'object' ? errData.detail : {};
      const code = detail.code || errData.code;
      const msg = detail.message || errData.detail || errData.message || '';
      if (err?.response?.status === 403 &&
          (code === 'PHONE_VERIFICATION_REQUIRED' || /verify your phone/i.test(msg))) {
        setShowPhoneGate(true);
      } else {
        toast.error(msg || 'Withdrawal failed');
      }
    } finally { setProcessing(false); }
  };

  return (
    <div
      className="vl-pirate-modal absolute inset-0 z-40 flex items-end justify-center bg-black/70 px-0 py-0 backdrop-blur-sm sm:items-center sm:px-4 sm:py-5"
      data-testid={`${currentMode}-popup`}
    >
      <div
      className="vl-treasure-transfer relative flex h-full w-full flex-col overflow-hidden anim-scaleIn sm:h-auto sm:max-h-[88vh] sm:max-w-[760px] lg:max-w-[860px] sm:rounded-[28px] sm:shadow-2xl"
      style={{
        // Explicit dark palette — the `color-scheme: dark` hint (set globally
        // in index.css) keeps Chrome / Samsung Internet auto-dark from
        // re-inverting our already-dark UI, but we still force the gradient
        // here with a solid fallback so the popup is readable even on browsers
        // that ignore CSS color-scheme.
        background: isDeposit
          ? 'radial-gradient(circle at 86% -8%, rgba(217,166,46,0.16), transparent 28%), linear-gradient(180deg, #08241a 0%, #050b09 100%), #050b09'
          : 'radial-gradient(circle at 14% -8%, rgba(34,197,94,0.14), transparent 28%), linear-gradient(180deg, #092218 0%, #050b09 100%), #050b09',
        color: '#ffffff',
        colorScheme: 'dark',
      }}
    >
      {/* Generating-checkout full-screen overlay — shown while we call the
          gateway API. Iter 133: cinematic layered glow + breathing pulse so
          the wait feels intentional and premium, not stalled. */}
      {processing && isDeposit && selectedMethod?.payment_mode === 'GATEWAY' && (
        <div
          className="absolute inset-0 z-50 flex flex-col items-center justify-center px-6 text-center anim-fadeIn"
          style={{ background: 'rgba(10,14,23,0.94)', backdropFilter: 'blur(10px)' }}
          data-testid="checkout-loading-overlay"
        >
          {/* Layered ambient halos behind the logo */}
          <div className="relative mb-5 flex items-center justify-center" style={{ width: 132, height: 132 }}>
            <div
              aria-hidden="true"
              className="absolute inset-0 rounded-full anim-ambient-a pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.30), transparent 65%)' }}
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 rounded-full anim-ambient-b pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(108,92,231,0.22), transparent 65%)' }}
            />
            {/* Outer rotating ring */}
            <div
              className="absolute rounded-full animate-spin"
              style={{
                inset: '8px',
                border: '3px solid rgba(212,175,55,0.18)',
                borderTopColor: '#D4AF37',
                animationDuration: '1.4s',
              }}
            />
            {/* Inner counter-rotating ring (subtle depth) */}
            <div
              className="absolute rounded-full animate-spin"
              style={{
                inset: '20px',
                border: '2px solid rgba(245,215,110,0.10)',
                borderBottomColor: '#F5D87A',
                animationDuration: '2.2s',
                animationDirection: 'reverse',
              }}
            />
            <img
              src="/vegas-lobby-logo.webp"
              alt="Vegas Lobby"
              className="object-contain anim-breathe"
              style={{
                maxWidth: '52%',
                maxHeight: '52%',
                filter: 'drop-shadow(0 0 16px rgba(212,175,55,0.65))',
              }}
              data-testid="checkout-loading-logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <h3
            className="text-xl font-bold mb-2"
            style={{ background: 'linear-gradient(135deg,#D4AF37,#F5D87A)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}
          >
            Generating checkout URL…
          </h3>
          <p className="text-sm text-gray-400 max-w-xs">
            Securely connecting to {selectedMethod?.title || 'the payment provider'} for your ${parseFloat(amount || 0).toFixed(2)} load.
          </p>
          <span className="mt-4 inline-block px-3 py-1 rounded-full text-[11px] font-semibold anim-gold-pulse" style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.35)', color: '#F5D87A' }}>
            Please don't close this window
          </span>
        </div>
      )}
      {/* Header with wallet summary + close */}
      <div
        className="vl-treasure-transfer__header flex items-center justify-between gap-3 px-4 py-3 flex-shrink-0"
        style={{ borderBottom: `1px solid ${isDeposit ? 'rgba(108,92,231,0.25)' : 'rgba(34,197,94,0.25)'}` }}
      >
        <div className="min-w-0 flex-1 rounded-2xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Wallet</p>
              <h2 className="text-xl font-extrabold text-white truncate">{isDeposit ? 'Deposit' : 'Withdraw'}</h2>
              <p className="text-[11px] text-gray-500 truncate">{user?.username || user?.email}</p>
            </div>
            <div className="rounded-full px-3 py-1.5 text-sm font-bold tabular-nums" style={{ background: 'rgba(217,166,46,0.12)', border: '1px solid rgba(245,215,110,0.28)', color: '#FFE6A0' }}>
              Balance: ${walletBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <button
          onClick={close}
          className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-white/10 active:scale-95 transition-all flex-shrink-0 ml-2"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
          data-testid="popup-close"
          aria-label="Close"
        >
          <X className="w-6 h-6 text-white" />
        </button>
      </div>

      {!gatewayState?.payUrl && (
        <div className="px-4 pt-4 sm:px-5">
          <div className="grid grid-cols-2 rounded-2xl p-1" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="wallet-flow-toggle">
            {[
              { id: 'deposit', label: '+ Deposit' },
              { id: 'withdrawal', label: '- Withdraw' },
            ].map((item) => {
              const active = currentMode === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => switchFlow(item.id)}
                  disabled={processing || !!gatewayState}
                  className={`rounded-xl py-3 text-sm font-extrabold transition-all disabled:opacity-50 ${active ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                  style={active ? {
                    background: isDeposit
                      ? 'linear-gradient(135deg, #F5D76E, #C58A18)'
                      : 'linear-gradient(135deg, #7C2D12, #B45309 45%, #D4AF37)',
                    color: isDeposit ? '#171006' : '#FFF6D8',
                    border: isDeposit ? '1px solid rgba(255,255,255,0.24)' : '1px solid rgba(245,215,110,0.42)',
                    boxShadow: isDeposit
                      ? '0 12px 28px rgba(197,138,24,0.28)'
                      : '0 12px 28px rgba(124,45,18,0.34), inset 0 1px 0 rgba(255,246,216,0.18)',
                  } : {}}
                  data-testid={`wallet-flow-${item.id}`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-between gap-2 text-[11px] text-gray-500">
            <span>{stepLabel}</span>
            <span className="truncate">
              {showMethodStep ? 'Choose method' : showAmountStep ? 'Enter amount' : 'Add payout details'}
            </span>
          </div>
        </div>
      )}

      {/* Scrollable body */}
      <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 py-4 sm:px-5">
        {gatewayState?.payUrl ? (
          <div className="flex min-h-[62vh] flex-1 flex-col overflow-hidden rounded-3xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,175,55,0.22)' }} data-testid="embedded-checkout-panel">
            <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.24), rgba(108,92,231,0.22))' }}>
                {checkoutApproved ? (
                  <CheckCircle2 className="h-6 w-6 text-green-400" />
                ) : checkoutFailed ? (
                  <X className="h-6 w-6 text-red-400" />
                ) : (
                  <Loader2 className="h-6 w-6 animate-spin text-[#F5D87A]" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#D4AF37]">Secure Checkout</p>
                <h3 className="truncate text-base font-extrabold text-white">
                  {gatewayState.methodTitle || selectedMethod?.title || 'Payment'} · ${(gatewayState.usd_amount || parseFloat(amount) || 0).toFixed(2)}
                </h3>
              </div>
              <button
                type="button"
                onClick={copyGatewayLink}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl px-3 text-xs font-bold text-white transition-all active:scale-95"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                data-testid="copy-checkout-link"
              >
                <Copy className="h-4 w-4" /> Copy
              </button>
            </div>

            <div
              className="px-4 py-2 text-center text-xs font-semibold"
              style={{
                background: checkoutApproved ? 'rgba(34,197,94,0.14)' : checkoutFailed ? 'rgba(239,68,68,0.14)' : 'rgba(212,175,55,0.12)',
                color: checkoutApproved ? '#86efac' : checkoutFailed ? '#fca5a5' : '#F5D87A',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}
              data-testid="checkout-status-banner"
            >
              {checkoutApproved
                ? 'Payment verified. Your wallet has been updated.'
                : checkoutFailed
                  ? 'Payment was not completed. Go back and try another amount or method.'
                  : 'Complete payment here. Keep this panel open and VegasLobby will update your wallet automatically.'}
            </div>

            {gatewayState?.payUrl && !checkoutApproved && !checkoutFailed && (
              <div className="vl-paylink-actions" data-testid="payment-link-actions">
                <button
                  type="button"
                  onClick={copyGatewayLink}
                  className="vl-paylink-wood-btn"
                  data-testid="copy-payment-link"
                >
                  <Copy className="h-4 w-4" /> Copy link
                </button>
                <button
                  type="button"
                  onClick={openGatewayLink}
                  className="vl-paylink-wood-btn vl-paylink-wood-btn--primary"
                  data-testid="proceed-payment-link"
                >
                  <ExternalLink className="h-4 w-4" /> Proceed to pay
                </button>
              </div>
            )}

            {checkoutApproved || checkoutFailed ? (
              <div className="flex flex-1 items-center justify-center px-5 py-10 text-center">
                <div className="max-w-sm">
                  <div
                    className="mx-auto flex h-20 w-20 items-center justify-center rounded-full"
                    style={{ background: checkoutApproved ? 'rgba(34,197,94,0.16)' : 'rgba(239,68,68,0.16)' }}
                  >
                    {checkoutApproved ? (
                      <CheckCircle2 className="h-11 w-11 text-green-400" />
                    ) : (
                      <X className="h-11 w-11 text-red-400" />
                    )}
                  </div>
                  <h3 className="mt-5 text-2xl font-extrabold text-white">
                    {checkoutApproved ? 'Deposit confirmed' : 'Checkout not completed'}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400">
                    {checkoutApproved
                      ? `Your $${(gatewayState.usd_amount || parseFloat(amount) || 0).toFixed(2)} deposit has been credited.`
                      : 'No wallet credit was added for this attempt.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="relative flex min-h-[520px] flex-1 overflow-hidden bg-white" data-testid="checkout-frame-wrap">
                {checkoutFrameLoading && (
                  <div className="vl-paylink-loading absolute inset-0 z-10 flex flex-col items-center justify-center text-center">
                    <div className="vl-paylink-loader-scene" aria-hidden="true">
                      <span className="vl-paylink-loader-coin vl-paylink-loader-coin--one" />
                      <span className="vl-paylink-loader-coin vl-paylink-loader-coin--two" />
                      <div className="vl-paylink-slot-loader"><i>7</i><i>BAR</i><i>$</i></div>
                      <Sparkles className="vl-paylink-loader-spark" />
                    </div>
                    <p className="vl-paylink-loading__title">Preparing treasure checkout</p>
                    <p className="vl-paylink-loading__copy">Hold tight while the payment gate opens. Copy Link and Proceed to Pay stay ready above.</p>
                  </div>
                )}
                <iframe
                  title="VegasLobby secure payment checkout"
                  src={gatewayState.payUrl}
                  className="h-full min-h-[520px] w-full flex-1 border-0"
                  allow="payment; clipboard-read; clipboard-write; publickey-credentials-get"
                  allowPaymentRequest
                  sandbox="allow-forms allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-storage-access-by-user-activation"
                  onLoad={() => setCheckoutFrameLoading(false)}
                  data-testid="embedded-checkout-iframe"
                />
              </div>
            )}
          </div>
        ) : (
        <>
        {/* 1. Amount */}
        {showAmountStep && (
        <div className="order-1">
        {(() => {
          const isInstant = selectedMethod?.payment_mode === 'GATEWAY' && isDeposit;
          const allowedUsd = resolveAllowedAmounts(selectedMethod, isInstant);

          // GATEWAY with fixed amounts: show provider's USD amount buttons, no custom input
          if (isInstant && allowedUsd) {
            // INSTANT flow keeps `amount` in USD exactly as the player sees it.
            const selectedUsd = amount ? parseFloat(amount) : null;
            return (
              <div className="space-y-3">
                <div
                  className="rounded-2xl px-4 py-3 text-center transition-all"
                  style={selectedUsd !== null
                    ? { background: 'linear-gradient(135deg, rgba(34,197,94,0.14), rgba(108,92,231,0.14))', border: '1px solid rgba(34,197,94,0.4)', boxShadow: '0 0 18px rgba(34,197,94,0.12)' }
                    : { background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.2)' }
                  }
                  data-testid="selected-amount-panel"
                >
                  {selectedUsd !== null ? (
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider text-gray-400">You pay</span>
                      <span className="text-3xl font-extrabold text-white tabular-nums" data-testid="selected-amount-usd">${selectedUsd.toFixed(2)}</span>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 py-1">
                      Pick an approved amount after selecting <span className="font-semibold text-white">{selectedMethod?.title || selectedMethod?.name || 'a payment method'}</span>
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-gray-400">Approved amounts</label>
                    <span className="text-[10px] text-gray-500" data-testid="amounts-count">{allowedUsd.length} options</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 max-h-[220px] overflow-y-auto pr-1 scrollbar-hide sm:grid-cols-4" data-testid="amounts-grid">
                    {allowedUsd.map(usd => {
                      const exactUsdStr = usd.toFixed(2);
                      const isSel = selectedUsd !== null && Math.abs(selectedUsd - usd) < 0.005;
                      return (
                        <button
                          key={usd}
                          onClick={() => !gatewayState && setAmount(exactUsdStr)}
                          disabled={!!gatewayState}
                          className="py-2 px-1 rounded-xl text-xs font-bold transition-all active:scale-95 disabled:opacity-40"
                          style={isSel
                            ? { background: accentGradient, color: '#fff', boxShadow: '0 0 0 2px #fff inset, 0 4px 12px rgba(108,92,231,0.35)' }
                            : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#ddd' }}
                          data-testid={`allowed-amount-${usd}`}
                        >
                          <div className="text-sm leading-tight">${usd}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div>
              <label className="text-sm font-semibold text-gray-300 mb-3 block">Amount</label>
              <div className="rounded-2xl px-5 py-4 mb-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-black text-white">$</span>
                  <Input
                    type="number" inputMode="decimal" value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0"
                    disabled={!!gatewayState}
                    className="h-14 border-0 bg-transparent p-0 text-4xl font-black text-white shadow-none focus-visible:ring-0 disabled:opacity-70"
                    data-testid="popup-amount-input"
                  />
                </div>
              </div>
              {!isDeposit && (
                <div className="mb-3 rounded-xl border border-yellow-400/20 bg-yellow-400/10 px-3 py-2 text-xs font-semibold text-yellow-200">
                  Minimum withdrawal is ${MIN_WITHDRAW_AMOUNT}.
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 mb-3 sm:grid-cols-6">
                {(isDeposit ? [5, 10, 20, 30, 50, 100] : [30, 50, 100]).map(v => (
                  <button
                    key={v} onClick={() => !gatewayState && setAmount(v.toString())}
                    disabled={!!gatewayState}
                    className="py-3 rounded-xl text-sm font-bold transition-all hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={amount === v.toString()
                      ? { background: accentGradient, color: '#fff' }
                      : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: '#ccc' }}
                    data-testid={`quick-amount-${v}`}
                  >
                    ${v}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-end gap-1.5 h-5" data-testid="popup-usd-equivalent">
                {amount && parseFloat(amount) > 0 ? (
                  <>
                    <span className="text-xs text-gray-500">Total</span>
                    <span className="text-sm font-bold text-green-400 tabular-nums">${parseFloat(amount).toFixed(2)}</span>
                  </>
                ) : (
                  <span className="text-[11px] text-gray-600">Amount in USD</span>
                )}
              </div>
            </div>
          );
        })()}
        </div>
        )}

        {/* 2. Payment Method */}
        {showMethodStep && (
        <div className="order-2">
          <label className="text-sm font-semibold text-gray-300 mb-3 block">Payment Method</label>
          <div className="grid grid-cols-1 gap-2.5 anim-stagger-fast sm:grid-cols-2">
            {visibleMethods
              // Iter 142.2: hide methods marked ``broken`` upstream
              // (e.g. PayPal — no enabled provider supports it). Methods
              // marked ``degraded`` (e.g. Google Pay routes via CashApp)
              // are still shown but with a warning badge so the player
              // knows what'll happen at checkout.
              .map(m => (
              <button
                key={m.id} onClick={() => {
                  if (!gatewayState) {
                    trackEvent('payment_method_selected', {
                      mode: currentMode,
                      payment_method: m.method || m.code || m.gateway_method_code,
                      payment_mode: m.payment_mode,
                    });
                    setSelectedMethod(m);
                    setAmount('');
                    setPopupStep('amount');
                  }
                }}
                disabled={!!gatewayState}
                className="px-3 py-2 rounded-xl text-left anim-card-lift anim-press disabled:opacity-60 disabled:cursor-not-allowed"
                style={selectedMethod?.id === m.id
                  ? { background: `${accent}18`, border: `2px solid ${accent}`, boxShadow: `0 0 0 4px ${accent}10, 0 6px 18px ${accent}25` }
                  : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }
                }
                data-testid={`method-${m.id}`}
              >
                <div className="flex items-center gap-2.5">
                  <PaymentMethodLogo method={m} />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[15px] text-white truncate leading-tight">{m.title || m.name}</p>
                    <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                      {m.payment_mode === 'GATEWAY' && isDeposit && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-semibold">INSTANT LOAD</span>
                      )}
                      {m.bonus_percent > 0 && isDeposit && <span className="text-[10px] text-green-400 font-semibold">+{m.bonus_percent}% Bonus</span>}
                      {m.upstream_status === 'degraded' && (
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-semibold"
                          title={m.upstream_reason || 'Routes via CashApp'}
                          data-testid={`method-degraded-${m.id}`}
                        >
                          ROUTED
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
            {visibleMethods.length === 0 && <p className="col-span-2 text-center text-gray-500 py-4 text-sm">No payment methods available</p>}
          </div>
          {/* Iter 142.2: degraded-method warning expanded under the grid
              when the player picks an affected method. Tells them BEFORE
              checkout that their tap will be routed via CashApp. */}
          {selectedMethod?.upstream_status === 'degraded' && selectedMethod?.upstream_reason && (
            <div
              className="mt-2 rounded-lg px-3 py-2 text-[12px] flex items-start gap-2"
              style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}
              data-testid={`degraded-warning-${selectedMethod.id}`}
            >
              <span className="text-amber-300 font-semibold shrink-0">Heads up:</span>
              <span className="text-gray-300">{selectedMethod.upstream_reason}</span>
            </div>
          )}
        </div>
        )}

        {/* 3. Withdrawal payout details */}
        {selectedMethod && !isDeposit && showPayoutStep && (
          <div className="order-4">
            <label className="text-sm font-semibold text-gray-300 mb-3 block">3. Your Payout Details</label>
            <div className="space-y-3">
              <div className="rounded-2xl px-4 py-3" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.24)' }}>
                <p className="text-sm font-semibold text-white">{payoutCopy?.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-gray-300">
                  Add the destination where admin should send your withdrawal. You can type the details, upload a QR, or do both.
                </p>
              </div>
              <div>
                <label className="text-[11px] text-gray-500 mb-1 block">{payoutCopy?.primaryLabel}</label>
                <Input
                  value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)}
                  placeholder={payoutCopy?.primaryPlaceholder}
                  className="h-12 rounded-2xl"
                  style={{ background: 'rgba(0,0,0,0.3)', borderColor: 'rgba(255,255,255,0.1)', color: '#fff' }}
                  data-testid="popup-wallet-address"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 mb-1 block">{payoutCopy?.secondaryLabel}</label>
                <Input
                  value={payoutTag} onChange={(e) => setPayoutTag(e.target.value)}
                  placeholder={payoutCopy?.secondaryPlaceholder}
                  className="h-12 rounded-2xl"
                  style={{ background: 'rgba(0,0,0,0.3)', borderColor: 'rgba(255,255,255,0.1)', color: '#fff' }}
                  data-testid="popup-payout-tag"
                />
              </div>
              <Input
                value={payoutDetails} onChange={(e) => setPayoutDetails(e.target.value)}
                placeholder="Additional notes (optional)"
                className="h-12 rounded-2xl"
                style={{ background: 'rgba(0,0,0,0.3)', borderColor: 'rgba(255,255,255,0.1)', color: '#fff' }}
              />

              {/* iter 128: Player's payment QR — admin scans this to send funds.
                  Optional but strongly recommended for cashtag/crypto methods
                  so the admin doesn't have to manually re-type the address. */}
              <div>
                <label className="text-[11px] text-gray-500 mb-1 block">
                  Your Payment QR <span className="text-gray-600">(recommended — admin will scan to send)</span>
                </label>
                <input
                  type="file"
                  ref={qrInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setQrFile(e.target.files[0])}
                  data-testid="withdraw-qr-file-input"
                />
                <button
                  type="button"
                  onClick={() => qrInputRef.current?.click()}
                  className="w-full rounded-2xl px-4 py-3 flex items-center justify-between"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px dashed rgba(255,255,255,0.15)' }}
                  data-testid="withdraw-qr-upload-btn"
                >
                  {qrFile ? (
                    <>
                      <span className="text-sm text-green-400 truncate">{qrFile.name}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setQrFile(null); }}
                        className="text-gray-400 hover:text-red-400"
                        data-testid="withdraw-qr-clear-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-gray-400">{payoutCopy?.qrHint}</span>
                      <Upload className="w-4 h-4 text-gray-500" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* INSTANT checkout hint */}
        {selectedMethod?.payment_mode === 'GATEWAY' && isDeposit && showAmountStep && !gatewayState && (
          <div
            className="order-4 rounded-2xl p-4 space-y-2"
            style={{
              background: 'linear-gradient(135deg, rgba(108,92,231,0.14), rgba(34,197,94,0.08))',
              border: '1px solid rgba(108,92,231,0.35)',
            }}
            data-testid="instant-checkout-hint"
          >
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#A28BFC]" />
              <p className="text-sm font-semibold text-white">Instant payment via {selectedMethod.title}</p>
            </div>
              <p className="text-xs text-gray-300 leading-relaxed">
              Tap <span className="font-semibold text-white">Pay</span>. The secure checkout opens inside VegasLobby and your wallet credits automatically once payment is confirmed. No screenshot needed.
            </p>
          </div>
        )}

        {/* 3. Deposit — Payment Details (hidden for INSTANT/GATEWAY — checkout link handles it) */}
        {selectedMethod && isDeposit && showAmountStep && selectedMethod.payment_mode !== 'GATEWAY' && (
          <div className="order-4">
            <label className="text-sm font-semibold text-gray-300 mb-3 block">Send Payment To</label>
            <div className="rounded-xl p-3.5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              {selectedMethod.admin_note && (
                <div className="rounded-lg p-3 mb-3" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)' }}>
                  <p className="text-xs text-[#F5D76E] font-medium">{selectedMethod.admin_note}</p>
                </div>
              )}
              {selectedMethod.method_details && <p className="text-sm text-gray-300 mb-3">{selectedMethod.method_details}</p>}
              {(() => {
                const qrUrl = selectedMethod.qr_code_url || selectedMethod.qr_image_url;
                if (!qrUrl) return null;
                const fullUrl = resolveAssetUrl(qrUrl, '');
                return (
                  <div className="flex flex-col items-center mb-3">
                    <div className="bg-white rounded-xl p-2.5"><img src={fullUrl} alt="QR" className="w-40 h-40 object-contain" /></div>
                    <p className="text-[10px] text-gray-500 mt-2">Scan QR Code to Pay</p>
                  </div>
                );
              })()}
              {(selectedMethod.receiving_address || selectedMethod.method_details) && (
                <div className="rounded-lg p-3" style={{ background: 'rgba(0,0,0,0.3)' }}>
                  <p className="text-[10px] text-gray-500 mb-1">Send payment to:</p>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-mono text-white break-all flex-1">{selectedMethod.receiving_address || selectedMethod.method_details}</p>
                    <button onClick={() => { navigator.clipboard.writeText(selectedMethod.receiving_address || selectedMethod.method_details); toast.success('Copied!'); }} className="text-[#D4AF37] flex-shrink-0 p-1"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
              {!selectedMethod.qr_code_url && !selectedMethod.qr_image_url && !selectedMethod.receiving_address && !selectedMethod.method_details && (
                <p className="text-sm text-red-300 text-center py-3">This manual payment method is missing admin payment details. Please choose another method or contact support.</p>
              )}
            </div>
          </div>
        )}

        {/* 4. Proof upload (manual deposit only — INSTANT skips this) */}
        {selectedMethod && isDeposit && showAmountStep && hasValidAmount && selectedMethod.payment_mode !== 'GATEWAY' && (
          <div className="order-5">
            <label className="text-sm font-semibold text-gray-300 mb-3 block">4. Upload Payment Screenshot</label>
            <input type="file" ref={proofInputRef} accept="image/*" className="hidden" onChange={(e) => setProofFile(e.target.files[0])} />
            <div onClick={() => proofInputRef.current?.click()}
              className="rounded-xl p-5 text-center cursor-pointer transition-all hover:border-[#D4AF37]/50"
              style={{ border: '2px dashed rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)' }}
              data-testid="popup-upload-proof">
              {proofFile ? (
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-green-400 truncate">{proofFile.name}</span>
                  <button onClick={(e) => { e.stopPropagation(); setProofFile(null); }} className="text-gray-400 hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div>
                  <ArrowUpCircle className="w-8 h-8 mx-auto mb-2 text-gray-600" />
                  <p className="text-sm text-gray-400">Tap to upload payment screenshot</p>
                  <p className="text-xs text-gray-600">PNG, JPG, WebP</p>
                </div>
              )}
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* Sticky footer submit */}
      <div className="px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex-shrink-0 sm:px-5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {gatewayState ? (
          <button
            onClick={close}
            className="w-full py-3.5 rounded-full font-bold text-sm transition-all bg-white/5 border border-white/10 hover:bg-white/10"
            data-testid="popup-footer-close"
          >
            {checkoutApproved ? 'Done' : checkoutFailed ? 'Close checkout' : "Close — we'll keep tracking the payment"}
          </button>
        ) : (
          <div className="flex gap-2">
            {!showMethodStep && (
              <button
                type="button"
                onClick={() => setPopupStep(showPayoutStep ? 'amount' : 'method')}
                disabled={processing}
                className="w-28 rounded-full border border-white/10 bg-white/5 py-3.5 text-sm font-bold text-white transition-all active:scale-[0.98] disabled:opacity-40"
                data-testid="popup-back-step"
              >
                Back
              </button>
            )}
            <button
              onClick={() => {
                if (showMethodStep) return;
                if (!isDeposit && showAmountStep) {
                  if (!hasValidAmount) { toast.error('Enter valid amount'); return; }
                  if (withdrawBelowMinimum) { toast.error(`Minimum withdrawal is $${MIN_WITHDRAW_AMOUNT}`); return; }
                  setPopupStep('payout');
                  return;
                }
                return isDeposit ? handleDeposit() : handleWithdraw();
              }}
              disabled={processing || !selectedMethod || (showAmountStep && !hasValidAmount) || (isDeposit && showAmountStep && selectedMethod?.payment_mode !== 'GATEWAY' && !proofFile) || (showPayoutStep && !submitReady)}
              className="flex-1 py-3.5 rounded-full font-bold text-sm transition-all disabled:opacity-40 active:scale-[0.98]"
              style={{ background: accentGradient, color: '#fff' }}
              data-testid="popup-submit"
            >
              {processing
                ? <div className="spinner w-5 h-5 mx-auto" />
                : showMethodStep
                  ? 'Select a Payment Method'
                  : !isDeposit && showAmountStep
                  ? 'Continue to Payout Details'
                  : isDeposit && selectedMethod?.payment_mode === 'GATEWAY'
                    ? `Pay $${amount ? parseFloat(amount).toFixed(2) : '0.00'} — Instant Load`
                    : `Submit ${isDeposit ? 'Deposit' : 'Withdrawal'} $${amount ? parseFloat(amount).toFixed(2) : '0.00'}`
              }
            </button>
          </div>
        )}
      </div>

      <PhoneVerificationGate
        open={showPhoneGate}
        onClose={() => setShowPhoneGate(false)}
        onVerified={() => {
          setShowPhoneGate(false);
          toast.success('Phone verified — you can submit your withdrawal now');
        }}
      />
      </div>
    </div>
  );
}
